import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, AlertCircle, Building2, Pause, Play, ArrowRight } from 'lucide-react';
import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/glitch-ui/slider';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationDataAtom, googleAdsAccountAtom, campaignsDataAtom } from '@/store';
import {
  businessQueryOptions,
  campaignTaskTrackingQueryOptions,
  metaIntegrationStatusQueryOptions,
  useCampaignBudgetMutationOptions,
  useUpdateBusinessMutation,
} from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useCampaignDraftFields } from '@/hooks/useCampaignDraft';
import { toast } from '@/hooks/useToast';
import type { CampaignData } from '@/lib/types';
import { getCampaignType, getCampaignTypeLabel as getCampaignTypeLabelUtil, CAMPAIGN_STATE } from '@/lib/types';
import { updateCampaignState, updateOrganizationBudget } from '@/lib/apis';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { MetaBudgetTab, useMetaAllocatedDailyWhole, type MetaBudgetTabApi } from '@/components/business/meta-budget-tab';

// Status icon for one campaign row in the budget allocation table. Extracted so it can
// call useQuery for task-tracking (an inline IIFE in the row map can't, since hooks must
// run at the top of a component). Mirrors the campaign-sidebar precedence — see #4412/#4413.
function CampaignStatusIcon({
  campaign,
  isToggling,
  isPaused,
  hasPendingDeploy,
}: {
  campaign: CampaignData;
  isToggling: boolean;
  isPaused: boolean;
  hasPendingDeploy: boolean;
}) {
  const { data: taskTracking } = useQuery({
    ...campaignTaskTrackingQueryOptions(campaign.id),
    enabled: campaign.state === CAMPAIGN_STATE.DRAFT,
  });

  const hasGenerationFailed = Boolean(
    campaign.state === CAMPAIGN_STATE.DRAFT &&
    taskTracking &&
    (() => {
      const cc = (taskTracking as { campaign_creation?: {
        failed?: number;
        stuck_in_progress?: number;
        in_progress?: number;
        restart_attempt_count?: number;
      } }).campaign_creation;
      if (!cc) return false;
      const failed = cc.failed ?? 0;
      const stuck = cc.stuck_in_progress ?? 0;
      const inProgress = cc.in_progress ?? 0;
      const restartAttempts = cc.restart_attempt_count ?? 0;
      const isActivelyRetrying = inProgress > 0 && restartAttempts > 0;
      return (failed > 0 || stuck > 0) && !isActivelyRetrying;
    })()
  );

  if (isToggling) return <Spinner size="sm" className="text-gray-400" />;
  if (campaign.state === CAMPAIGN_STATE.ERROR || hasGenerationFailed) {
    return <AlertCircle className="w-4 h-4 text-red-500" />;
  }
  if (campaign.state === CAMPAIGN_STATE.LIVE && hasPendingDeploy) {
    return <AlertCircle className="w-4 h-4 text-amber-500" />;
  }
  if (campaign.state === CAMPAIGN_STATE.LIVE) {
    return <Play className="w-4 h-4 text-green-500" />;
  }
  if (isPaused) {
    return <Pause className="w-4 h-4 text-amber-500" />;
  }
  // Never-published paused or draft
  return <AlertCircle className="w-4 h-4 text-amber-500" />;
}

interface CampaignBudgetAllocation {
  campaignId: number;
  budget: number;
}

// Platform config — enabled state is set dynamically based on account connections
const PLATFORM_CONFIG = [
  { id: 'google', label: 'Google Ads', logo: '/google-ads.svg' },
  { id: 'meta', label: 'Meta', logo: '/meta.png' },
] as const;


function parseCampaignName(name: string): string {
  const parts = name.split(' | ');
  if (parts.length >= 3 && parts[0] === 'Glitch') {
    if (parts.length === 4) {
      if (parts[1] === 'Geo') return `${parts[3]} | ${parts[2]}`;
      return parts[3];
    }
    return parts[2];
  }
  return name;
}

function getCampaignTypeLabel(campaign: CampaignData): string {
  return getCampaignTypeLabelUtil(getCampaignType(campaign));
}

function getCampaignTypeStyle(campaign: CampaignData): string {
  const type = getCampaignType(campaign);
  switch (type) {
    case 'brand': return 'bg-purple-100 text-purple-800';
    case 'competitor': return 'bg-orange-100 text-orange-800';
    case 'prospecting': return 'bg-blue-100 text-blue-800';
    case 'onboarding': return 'bg-indigo-100 text-indigo-800';
    case 'hipo': return 'bg-pink-100 text-pink-700';
    case 'icp': return 'bg-teal-100 text-teal-700';
    case 'location': return 'bg-indigo-100 text-indigo-700';
    default: return 'bg-gray-100 text-gray-800';
  }
}

interface BudgetV2Props {
  highlightCampaignId?: number;
  /** Open on a specific tab (e.g. the Meta campaign page's Manage Budget opens on 'meta') (#5981). */
  initialTab?: 'all' | 'google' | 'meta';
  onDirtyChange?: (isDirty: boolean) => void;
  onValidationChange?: (hasError: boolean) => void;
  onSaveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

export function BudgetV2({ highlightCampaignId, initialTab, onDirtyChange, onValidationChange, onSaveRef }: BudgetV2Props = {}) {
  const navigate = useNavigate();
  const organizationData = useAtomValue(organizationDataAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const hasGoogleAdsAccount = !!googleAdsAccount?.googleads_account_id;
  // #4288 Phase 2a: the Meta row is live when the integration is connected and an ad account is
  // selected. The org-level platform-cap slider writes to ``Business.platform_budgets[Meta]``
  // like Google's does. Shown for everyone (#5976, #6678).
  const { data: metaIntegrationStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const hasMetaConnected = !!metaIntegrationStatus?.is_connected && !!metaIntegrationStatus?.selected_ad_account;
  // The Meta platform-cap row is editable for everyone, connected or not. A Meta cap can exist
  // before the integration does (onboarding splits the ceiling 50/50, #6186), and a disabled
  // row locked users out of zeroing a cap that still counted against Maximum Budget. Anything
  // that depends on actual Meta campaign data still keys on hasMetaConnected.
  const PLATFORMS = PLATFORM_CONFIG.map(p => ({
    ...p,
    enabled: p.id === 'google' ? hasGoogleAdsAccount : true,
  }));
  const allCampaigns = useAtomValue(campaignsDataAtom);
  // Unmanaged externals stay out of budget math; a MANAGED campaign (#6381 chunk D)
  // is budgeted like any native campaign.
  const campaigns = useMemo(() => allCampaigns.filter(c => c.origin !== 'external' || !!c.managed_at), [allCampaigns]);
  const { symbol: currencySymbol } = useCurrencySymbol();
  const cs = currencySymbol ?? '';
  const { draft, updateDraft, syncDraft } = useCampaignDraftFields();
  const [togglingCampaignId, setTogglingCampaignId] = useState<number | null>(null);
  const [pauseConfirmCampaign, setPauseConfirmCampaign] = useState<CampaignData | null>(null);
  const [sortKey, setSortKey] = useState<'name' | 'type' | 'budget'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [platformSortKey, setPlatformSortKey] = useState<'name' | 'budget'>('name');
  const [platformSortDir, setPlatformSortDir] = useState<'asc' | 'desc'>('asc');
  const [showAutoPopulateBanner, setShowAutoPopulateBanner] = useState(false);
  const highlightRowRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);
  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const togglePlatformSort = (key: typeof platformSortKey) => {
    if (platformSortKey === key) setPlatformSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPlatformSortKey(key); setPlatformSortDir('asc'); }
  };

  // Fetch business data for platform_budgets
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const [activeTab, setActiveTab] = useState<'all' | 'google' | 'meta'>(() => {
    return initialTab ?? (highlightCampaignId ? 'google' : 'all');
  });

  // Tier 1: Maximum daily budget — initialized synchronously to avoid false-positive dirty state
  // before the init effect fires (effects run after paint; the blocker is live from first render).
  const [maxDaily, setMaxDaily] = useState(() =>
    organizationData?.max_spend ? Math.round(organizationData.max_spend / 30) : 0
  );

  // Tier 2: Platform budgets (monthly) — same reason: init from cached React Query data if available.
  const [platformBudgets, setPlatformBudgets] = useState<Record<string, number>>(() => ({
    google: business?.platform_budgets?.find(b => b.platform === 'Google')?.amount ?? 0,
    meta: business?.platform_budgets?.find(b => b.platform === 'Meta')?.amount ?? 0,
  }));

  // Tier 3: Per-campaign allocation (daily) — campaignsDataAtom is synchronous; init from it directly.
  const [allocations, setAllocations] = useState<CampaignBudgetAllocation[]>(() =>
    campaigns.map(c => ({ campaignId: c.id, budget: c.budget || 0 }))
  );

  // Init from org/account/campaign data
  useEffect(() => {
    if (organizationData?.max_spend) {
      setMaxDaily(Math.round(organizationData.max_spend / 30));
    }
  }, [organizationData?.max_spend]);

  useEffect(() => {
    const googlePlatformBudget = business?.platform_budgets?.find(b => b.platform === 'Google')?.amount;
    if (googlePlatformBudget != null) {
      setPlatformBudgets(prev => ({ ...prev, google: googlePlatformBudget }));
    }
    // Read back the saved Meta cap too, so it doesn't reset to 0 on refresh (#3824 fix).
    const metaPlatformBudget = business?.platform_budgets?.find(b => b.platform === 'Meta')?.amount;
    if (metaPlatformBudget != null) {
      setPlatformBudgets(prev => ({ ...prev, meta: metaPlatformBudget }));
    }
  }, [business?.platform_budgets]);

  useEffect(() => {
    if (campaigns.length > 0 && allocations.length === 0) {
      setAllocations(campaigns.map(c => ({
        campaignId: c.id,
        budget: c.budget || 0,
      })));
    }
  }, [campaigns, allocations.length]);

  // The Meta side of the first-run reconciliation below — null while ABO details are loading.
  const metaAllocatedDaily = useMetaAllocatedDailyWhole(hasMetaConnected);

  // Auto-populate max daily and platform budget for existing users who haven't configured Budget V2
  useEffect(() => {
    const orgId = organizationData?.id;
    if (!orgId) return;
    const storageKey = `budget_v2_populated_${orgId}`;
    if (sessionStorage.getItem(storageKey)) return;

    const maxSpendEmpty = !organizationData?.max_spend;
    const platformBudgetsEmpty = !business?.platform_budgets?.length ||
      !business.platform_budgets.find(b => b.platform === 'Google')?.amount;
    const sumDaily = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
    // Meta campaigns count too (#5981) — pre-5981 this split gave Google the entire ceiling and
    // left Meta at 0 even when Meta campaigns were allocated. Wait for the Meta figure before
    // deciding (null = ABO details still loading; the effect re-runs when they land).
    const metaSumDaily = hasMetaConnected ? metaAllocatedDaily : 0;
    if (metaSumDaily === null) return;
    // Also reconcile when the saved max is just a placeholder that live campaign spend
    // already exceeds (#4683 transition). Those accounts would otherwise show a false
    // "over budget" warning. Campaign budgets are the one trustworthy source (synced with
    // the platforms), so suggest reconciling the ceiling to actual spend — reviewed and saved
    // by the user, never silently persisted.
    const maxSpendInadequate = maxSpendEmpty || (sumDaily + metaSumDaily) * 30 > (organizationData?.max_spend ?? 0);

    if (maxSpendInadequate && platformBudgetsEmpty && (sumDaily > 0 || metaSumDaily > 0)) {
      sessionStorage.setItem(storageKey, 'true');
      const calculatedMaxDaily = Math.ceil(sumDaily) + Math.ceil(metaSumDaily);
      setMaxDaily(calculatedMaxDaily);
      // Each platform gets its own campaigns' allocation — the ceiling splits by what is actually
      // allocated, not "everything to Google".
      setPlatformBudgets(prev => ({
        ...prev,
        google: Math.ceil(sumDaily) * 30,
        ...(metaSumDaily > 0 ? { meta: Math.ceil(metaSumDaily) * 30 } : {}),
      }));

      setShowAutoPopulateBanner(true);
    }
  }, [organizationData?.max_spend, business?.platform_budgets, campaigns, metaAllocatedDaily, hasMetaConnected, organizationData?.id]);

  // Sync draft atom budget → local allocations for the current campaign
  useEffect(() => {
    if (draft?.campaignId != null && draft.budget != null) {
      setAllocations(prev => {
        const existing = prev.find(a => a.campaignId === draft.campaignId);
        if (!existing || existing.budget === draft.budget) return prev;
        return prev.map(a =>
          a.campaignId === draft.campaignId ? { ...a, budget: draft.budget } : a
        );
      });
    }
  }, [draft?.campaignId, draft?.budget]);

  // Computed values
  const totalAllocatedDaily = useMemo(() => allocations.reduce((sum, a) => sum + a.budget, 0), [allocations]);
  const totalAllocatedMonthly = totalAllocatedDaily * 30;
  const googleBudget = platformBudgets.google;
  // Both platforms' caps count toward Maximum Budget; both rows are on screen (#6678).
  const platformTotalMonthly = platformBudgets.google + platformBudgets.meta;

  const googleBudgetDaily = Math.floor(googleBudget / 30);
  const savedMaxDaily = Math.round((organizationData?.max_spend ?? 0) / 30);

  // Max budget is the ceiling — platform budgets must not exceed it.
  // No auto-sync: if platform total > max, validation (isPlatformOverTotal) shows the error.

  const maxDailyMonthly = maxDaily * 30;
  const maxDailyDelta = maxDaily - savedMaxDaily;

  // Slider max for campaign allocation
  const sliderMax = useMemo(() => {
    if (googleBudgetDaily > 0) return Math.max(googleBudgetDaily, 10);
    if (maxDaily > 0) return Math.max(maxDaily, 10);
    return 100;
  }, [googleBudgetDaily, maxDaily]);

  // Platform slider max
  const platformSliderMax = useMemo(() => {
    if (maxDailyMonthly > 0) return maxDailyMonthly;
    return Math.max(platformTotalMonthly * 2, 1000);
  }, [maxDailyMonthly, platformTotalMonthly]);


  const campaignInputsDisabled = maxDaily <= 0 && googleBudget <= 0 && totalAllocatedDaily <= 0;

  // allowZero is true for draft (not yet live) and paused (#5470) campaigns: a paused campaign can drop
  // to 0 to reallocate its full budget to others. The backend keeps that 0 local-only (never synced to
  // Google, which rejects 0). A live campaign floors at 1, the budget Google enforces while serving.
  const handleCampaignBudgetChange = (campaignId: number, budget: number, allowZero: boolean) => {
    const minBudget = allowZero ? 0 : 1;
    const roundedBudget = Math.max(minBudget, Math.round(budget));
    setAllocations(prev => prev.map(a => a.campaignId === campaignId ? { ...a, budget: roundedBudget } : a));
    if (draft?.campaignId === campaignId) {
      updateDraft('budget', roundedBudget);
    }
    setSaveError(null);
  };

  const handleTogglePause = async (campaign: CampaignData) => {
    const newState = campaign.state === CAMPAIGN_STATE.PAUSED ? CAMPAIGN_STATE.LIVE : CAMPAIGN_STATE.PAUSED;
    setTogglingCampaignId(campaign.id);
    try {
      await updateCampaignState(campaign.id, newState);
      await queryClient.refetchQueries({ queryKey: ['campaigns'] });
    } catch {
      toast({
        title: 'Failed to update campaign',
        description: `Could not ${newState === CAMPAIGN_STATE.PAUSED ? 'pause' : 'resume'} the campaign. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setTogglingCampaignId(null);
    }
  };

  // Validation
  const isPlatformOverTotal = platformTotalMonthly > maxDailyMonthly;
  const isCampaignsOverPlatform = totalAllocatedMonthly > googleBudget;
  const isPlatformFullyAllocated = platformTotalMonthly > 0 && platformTotalMonthly === maxDailyMonthly && !isPlatformOverTotal;
  const isFullyAllocated = googleBudget > 0 && totalAllocatedMonthly === googleBudget && !isCampaignsOverPlatform;

  // Discard handler
  const handleDiscardChanges = useCallback(() => {
    setAllocations(campaigns.map(c => ({
      campaignId: c.id,
      budget: c.budget || 0,
    })));
    // Reset draft budget for the current campaign to its server value
    if (draft?.campaignId) {
      const currentCampaign = campaigns.find(c => c.id === draft.campaignId);
      if (currentCampaign) {
        syncDraft('budget', currentCampaign.budget || 0);
      }
    }
    const googlePlatformBudget = business?.platform_budgets?.find(b => b.platform === 'Google')?.amount;
    if (googlePlatformBudget != null) {
      setPlatformBudgets(prev => ({ ...prev, google: googlePlatformBudget }));
    } else {
      setPlatformBudgets(prev => ({ ...prev, google: 0 }));
    }
    if (organizationData?.max_spend) {
      setMaxDaily(Math.round(organizationData.max_spend / 30));
    }
  }, [campaigns, business?.platform_budgets, organizationData?.max_spend, draft?.campaignId, syncDraft]);

  // Mutations
  const queryClient = useQueryClient();
  const businessMutation = useUpdateBusinessMutation(organizationData?.id || 0);
  const campaignBudgetMutation = useCampaignBudgetMutationOptions();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const maxDailyChanged = useMemo(() => {
    const savedMaxSpend = organizationData?.max_spend ?? 0;
    return maxDaily !== Math.round(savedMaxSpend / 30);
  }, [maxDaily, organizationData?.max_spend]);

  const platformBudgetsChanged = useMemo(() => {
    // Saved baseline is the persisted platform_budgets value, or 0 when none is
    // set (#4683). Budgets are entered manually: an empty account starts at 0 and
    // any value the user types is detected as dirty and persists on Save.
    const savedGoogle = business?.platform_budgets?.find(b => b.platform === 'Google')?.amount ?? 0;
    const googleChanged = platformBudgets.google !== savedGoogle;
    const savedMeta = business?.platform_budgets?.find(b => b.platform === 'Meta')?.amount ?? 0;
    const metaChanged = platformBudgets.meta !== savedMeta;
    return googleChanged || metaChanged;
  }, [platformBudgets.google, platformBudgets.meta, business?.platform_budgets]);

  const handleSavePlatformBudgets = useCallback(async () => {
    if (!business?.id) return;
    // Both caps persist together (#3824 fix — Meta persists like Google): the PATCH replaces
    // platform_budgets wholesale, and both rows are on screen for everyone (#6678).
    const platform_budgets = [
      { platform: 'Google', amount: platformBudgets.google },
      { platform: 'Meta', amount: platformBudgets.meta },
    ];
    await businessMutation.mutateAsync({
      businessId: business.id,
      data: { platform_budgets },
    });
  }, [business?.id, platformBudgets.google, platformBudgets.meta, businessMutation]);

  const changedAllocations = useMemo(() => {
    return allocations.filter(a => {
      const campaign = campaigns.find(c => c.id === a.campaignId);
      return a.budget !== (campaign?.budget || 0);
    });
  }, [allocations, campaigns]);

  const hasChanges = changedAllocations.length > 0 || platformBudgetsChanged || maxDailyChanged;

  // Meta tab's staged edits — the tab stages rows locally and exposes save/discard through this
  // handle so the shared Save Changes row below (and parent-managed save flows) drive it, giving
  // the Meta tab the same explicit-save behavior as Account Limits and Google Ads.
  const metaApiRef = useRef<MetaBudgetTabApi | null>(null);
  const [metaDirty, setMetaDirty] = useState(false);
  const [metaOverAllocated, setMetaOverAllocated] = useState(false);
  const [isMetaSaving, setIsMetaSaving] = useState(false);
  const [metaSaveError, setMetaSaveError] = useState<string | null>(null);
  const [metaSaveSuccess, setMetaSaveSuccess] = useState(false);

  const handleMetaSave = useCallback(async () => {
    if (!metaApiRef.current || !metaDirty || isMetaSaving) return;
    if (metaOverAllocated) {
      throw new Error('Budget validation failed. Resolve over-budget items before saving.');
    }
    setIsMetaSaving(true);
    setMetaSaveError(null);
    setMetaSaveSuccess(false);
    try {
      await metaApiRef.current.save();
      setMetaSaveSuccess(true);
      setTimeout(() => setMetaSaveSuccess(false), 2000);
    } catch (error) {
      setMetaSaveError(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error; // callers (tab-switch dialog, route blocker) need the failure
    } finally {
      setIsMetaSaving(false);
    }
  }, [metaDirty, metaOverAllocated, isMetaSaving]);

  // Expose dirty state, validation errors, and save function to parent
  useEffect(() => { onDirtyChange?.(hasChanges || metaDirty); }, [hasChanges, metaDirty, onDirtyChange]);
  useEffect(() => { onValidationChange?.(isPlatformOverTotal || isCampaignsOverPlatform || metaOverAllocated); }, [isPlatformOverTotal, isCampaignsOverPlatform, metaOverAllocated, onValidationChange]);

  const handleSaveCampaignBudgets = useCallback(async () => {
    // Save reductions first so the total goes down before increases push it up
    const sorted = [...changedAllocations].sort((a, b) => {
      const aCampaign = campaigns.find(c => c.id === a.campaignId);
      const bCampaign = campaigns.find(c => c.id === b.campaignId);
      const aDelta = a.budget - (aCampaign?.budget || 0);
      const bDelta = b.budget - (bCampaign?.budget || 0);
      return aDelta - bDelta;
    });
    for (const allocation of sorted) {
      await campaignBudgetMutation.mutateAsync({
        campaignId: allocation.campaignId,
        budgetUpdate: { budget: allocation.budget },
      });
    }
  }, [changedAllocations, campaigns, campaignBudgetMutation]);

  const handleSaveAll = useCallback(async () => {
    if (!hasChanges || isSaving) return;
    if (isPlatformOverTotal || isCampaignsOverPlatform) {
      throw new Error('Budget validation failed. Resolve over-budget items before saving.');
    }
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      if (isPlatformOverTotal || isCampaignsOverPlatform) {
        throw new Error('Budget validation failed. Resolve over-budget items before saving.');
      }
      // Save top-down: max daily → platform → campaigns
      // This ensures limits are raised before allocations, preventing validation failures
      if (maxDailyChanged && organizationData) {
        // Admin-gated budget endpoint that THROWS on failure (#5597) — so a 403/error surfaces via
        // the catch below instead of optimistically showing "Saved" and reverting on refresh.
        await updateOrganizationBudget(organizationData.id, maxDaily * 30);
        setOrganizationData({ ...organizationData, max_spend: maxDaily * 30 });
      }
      if (platformBudgetsChanged) await handleSavePlatformBudgets();
      if (changedAllocations.length > 0) await handleSaveCampaignBudgets();

      // Refetch server data so derived hasChanges resets.
      // #4676 — scope ['campaign', slug] invalidation to slugs we know belong
      // to the current org, instead of a blanket prefix invalidation. Blanket
      // invalidation fans out to stale subscriptions from previously-viewed
      // orgs whose slugs 404 on this user, which causes React Query to revert
      // the UI to its pre-save state and look like the save failed.
      const knownSlugs = new Set(campaigns.map(c => c.slug).filter((s): s is string => !!s));
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['organizations'] }),
        queryClient.refetchQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({
          predicate: q =>
            q.queryKey[0] === 'campaign' &&
            typeof q.queryKey[1] === 'string' &&
            knownSlugs.has(q.queryKey[1]),
        }),
        // A Meta-allocation change re-pushes each campaign's spend cap server-side (#6076) — the
        // detail pages' spend-cap captions must refetch. Same blanket prefix the Meta mutations use.
        queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['meta', 'campaign'] }),
        organizationData?.id ? queryClient.refetchQueries({ queryKey: ['organizations', organizationData.id, 'business'] }) : Promise.resolve(),
      ]);

      // Sync the draft atom for the current campaign to clear dirty state
      if (draft?.campaignId && draft.budget != null) {
        syncDraft('budget', draft.budget);
      }

      setShowAutoPopulateBanner(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown error';
      setSaveError(`Save failed: ${detail}`);
      // Refetch to show actual saved state (#4676 — same scoping as success path).
      const knownSlugsErr = new Set(campaigns.map(c => c.slug).filter((s): s is string => !!s));
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['organizations'] }),
        queryClient.refetchQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({
          predicate: q =>
            q.queryKey[0] === 'campaign' &&
            typeof q.queryKey[1] === 'string' &&
            knownSlugsErr.has(q.queryKey[1]),
        }),
        organizationData?.id ? queryClient.refetchQueries({ queryKey: ['organizations', organizationData.id, 'business'] }) : Promise.resolve(),
      ]);
      throw error;  // Re-throw so callers (tab-switch dialog, route blocker) know save failed
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, isSaving, maxDailyChanged, maxDaily, organizationData, platformBudgetsChanged, changedAllocations.length, handleSavePlatformBudgets, handleSaveCampaignBudgets, queryClient, draft, syncDraft, campaigns]);

  // Google + Meta in one pass — parents with onSaveRef (approval flow) and the unsaved-changes
  // guard must persist BOTH tabs' staged edits; each half no-ops when its tab is clean.
  const handleSaveEverything = useCallback(async () => {
    await handleSaveAll();
    if (metaDirty) await handleMetaSave();
  }, [handleSaveAll, metaDirty, handleMetaSave]);

  const handleDiscardEverything = useCallback(() => {
    handleDiscardChanges();
    metaApiRef.current?.discard();
  }, [handleDiscardChanges]);

  // Expose save function to parent via ref
  useEffect(() => { if (onSaveRef) onSaveRef.current = handleSaveEverything; }, [handleSaveEverything, onSaveRef]);

  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: (hasChanges && !showAutoPopulateBanner) || metaDirty,
    onSave: handleSaveEverything,
    onDiscard: handleDiscardEverything,
    isSaving: isSaving || isMetaSaving,
    contextName: 'Budget',
  });

  // Tab content renderers
  const renderAllPlatformsTab = () => (
    <div className="space-y-6">
      {showAutoPopulateBanner && (
        <div className="inline-flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-4 py-3">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Account limits have been pre-populated from your existing campaigns. Review and save to confirm.</span>
        </div>
      )}
      {/* The maximum first (#6673): the one number everything below divides.
          Under the platform rows it read as a total nobody could edit. */}
      <Card className="px-4 py-4">
        {/* The same grid as the platform table, so the input and the monthly
            figure sit in its Daily and Monthly columns. */}
        <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center">
          {/* The business's own mark where each platform row carries its
              platform's (#6712), so the total is a row like the rest. */}
          {organizationData?.primary_logo_url ? (
            <img
              src={organizationData.primary_logo_url}
              alt=""
              className="h-6 w-6 rounded-full border border-gray-200 bg-white object-contain"
            />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#290BD9] bg-opacity-10">
              <Building2 className="h-3.5 w-3.5 text-[#290BD9]" strokeWidth={1.5} />
            </span>
          )}
          <div className="col-span-3">
            <h3 className="text-sm font-semibold text-gray-900">Maximum daily budget</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              The most that is spent across every platform in a day. Set it here, then divide it between the platforms below.
            </p>
          </div>
          <div>
            <div className={`flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto transition-colors ${isPlatformOverTotal ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300 focus-within:border-[#290BD9]'}`}>
              <span className="text-sm text-gray-500">{cs}</span>
              <input
                type="text"
                value={maxDaily.toLocaleString()}
                onChange={(e) => {
                  const value = Math.max(0, parseFloat(e.target.value.replace(/,/g, '')) || 0);
                  setMaxDaily(value);
                  setSaveError(null);
                }}
                onFocus={(e) => e.target.select()}
                aria-label="Maximum daily budget"
                className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-full text-right focus:ring-0 p-0"
              />
            </div>
          </div>
          <div className="text-sm text-gray-500 text-center">~{cs}{maxDailyMonthly.toLocaleString()}/m</div>
        </div>
      </Card>

      {/* By Platform: dividing the maximum out */}
      <Card className="overflow-hidden">
        <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center px-4 py-2.5 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div></div>
          <SortableHeader sortDirection={platformSortKey === 'name' ? platformSortDir : false} onSort={() => togglePlatformSort('name')}>Platform</SortableHeader>
          <div></div>
          <SortableHeader sortDirection={platformSortKey === 'budget' ? platformSortDir : false} onSort={() => togglePlatformSort('budget')}>Allocation</SortableHeader>
          <div className="text-center"><SortableHeader sortDirection={platformSortKey === 'budget' ? platformSortDir : false} onSort={() => togglePlatformSort('budget')}>Daily</SortableHeader></div>
          <div className="text-center"><SortableHeader sortDirection={platformSortKey === 'budget' ? platformSortDir : false} onSort={() => togglePlatformSort('budget')}>Monthly</SortableHeader></div>
        </div>

        <div className="px-4">
          {[...PLATFORMS].sort((a, b) => {
            let cmp = 0;
            if (platformSortKey === 'name') cmp = a.label.localeCompare(b.label);
            else if (platformSortKey === 'budget') cmp = (platformBudgets[a.id] || 0) - (platformBudgets[b.id] || 0);
            return platformSortDir === 'asc' ? cmp : -cmp;
          }).map((platform) => {
            const budgetMonthly = platformBudgets[platform.id] || 0;
            const budgetDaily = Math.round(budgetMonthly / 30);
            const pct = platformSliderMax > 0 ? Math.min(budgetMonthly / platformSliderMax * 100, 100) : 0;

            return (
              <div key={platform.id} className={`grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center py-3 border-b border-gray-100 last:border-b-0 ${!platform.enabled ? '[&>*:not(:first-child)]:opacity-40' : ''}`}>
                <div className="flex items-center justify-center">
                  <img src={platform.logo} className="h-5 w-5 shrink-0 object-contain" alt={platform.label} />
                </div>
                <div>
                  <button onClick={() => setActiveTab(platform.id)} className="text-sm font-medium text-gray-900 hover:text-[#290BD9] hover:underline transition-colors cursor-pointer">{platform.label}</button>
                  {!platform.enabled && platform.id === 'google' && (
                    <span onClick={() => navigate({ to: '/settings/account', hash: 'integrations' })} className="text-xs text-[#290BD9] hover:text-[#2309C5] hover:underline mt-1 block cursor-pointer">
                      Connect to Google Ads
                    </span>
                  )}
                  {platform.id === 'meta' && !hasMetaConnected && (
                    <span onClick={() => navigate({ to: '/settings/account', hash: 'integrations' })} className="text-xs text-[#290BD9] hover:text-[#2309C5] hover:underline mt-1 block cursor-pointer">
                      Connect to Meta Ads
                    </span>
                  )}
                </div>
                <div></div>
                <div className="px-1">
                  <Slider
                    value={pct}
                    onChange={(value) => {
                      if (!platform.enabled) return;
                      const daily = Math.round(value / 100 * platformSliderMax / 30);
                      setPlatformBudgets(prev => ({ ...prev, [platform.id]: daily * 30 }));
                
                      setSaveError(null);
                    }}
                    disabled={!platform.enabled}
                  />
                </div>
                <div>
                  <div className={`flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto transition-colors ${isPlatformOverTotal ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300 focus-within:border-[#290BD9]'}`}>
                    <span className="text-sm text-gray-500">{cs}</span>
                    <input
                      type="text"
                      value={budgetDaily.toLocaleString()}
                      onChange={(e) => {
                        if (!platform.enabled) return;
                        const daily = Math.max(0, parseFloat(e.target.value.replace(/,/g, '')) || 0);
                        setPlatformBudgets(prev => ({ ...prev, [platform.id]: daily * 30 }));
                  
                        setSaveError(null);
                      }}
                      onFocus={(e) => e.target.select()}
                      disabled={!platform.enabled}
                      className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-full text-right focus:ring-0 p-0 disabled:text-gray-400"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-500 text-center">~{cs}{budgetMonthly.toLocaleString()}/m</div>
              </div>
            );
          })}
        </div>

        {/* How much of the maximum the platforms take: a summary, not an input */}
        <div className={`border-t px-4 py-3 ${isPlatformOverTotal ? 'bg-red-50' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center">
            <div></div>
            <div>
              <span className={`text-sm font-semibold ${isPlatformOverTotal ? 'text-red-700' : 'text-gray-900'}`}>Allocated</span>
              {isPlatformOverTotal ? (
                <span className="text-xs block text-red-600">{cs}{Math.round((platformTotalMonthly - maxDailyMonthly) / 30).toLocaleString()}/day over the maximum</span>
              ) : isPlatformFullyAllocated && maxDailyMonthly > 0 ? (
                <span className="text-xs block text-green-600">Fully allocated</span>
              ) : maxDailyMonthly > 0 ? (
                <span className="text-xs block text-amber-600">{cs}{Math.round((maxDailyMonthly - platformTotalMonthly) / 30).toLocaleString()}/day of the maximum still to allocate</span>
              ) : null}
            </div>
            <div></div>
            <div></div>
            <div className={`text-center text-sm font-medium ${isPlatformOverTotal ? 'text-red-700' : 'text-gray-900'}`}>
              {cs}{Math.round(platformTotalMonthly / 30).toLocaleString()} of {cs}{maxDaily.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500 text-center">~{cs}{platformTotalMonthly.toLocaleString()}/m</div>
          </div>
        </div>
      </Card>

      {/* Warnings */}
    </div>
  );

  const renderCampaignTab = (platformId: string) => {
    const platformBudget = platformBudgets[platformId] || 0;
    return (
    <div className="space-y-4">
      {/* Platform budget context */}
      {campaigns.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
          <img src="/google-ads.svg" className="h-10 w-10 mb-4 opacity-40" alt="Google Ads" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Google Campaigns Yet</h3>
          <p className="text-sm text-gray-500 max-w-md mb-6">
            Google campaign allocation will appear here once campaigns are created.
          </p>
          <button
            onClick={() => navigate({ to: '/campaigns' })}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
          >
            Go to Campaigns
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Card>
      ) : (
        <Card className={`overflow-hidden ${platformBudget <= 0 && totalAllocatedDaily <= 0 ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center px-4 py-2.5 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div></div>
            <SortableHeader sortDirection={sortKey === 'name' ? sortDir : false} onSort={() => toggleSort('name')}>Campaign</SortableHeader>
            <SortableHeader sortDirection={sortKey === 'type' ? sortDir : false} onSort={() => toggleSort('type')}>Type</SortableHeader>
            <SortableHeader sortDirection={sortKey === 'budget' ? sortDir : false} onSort={() => toggleSort('budget')}>Allocation</SortableHeader>
            <div className="text-center"><SortableHeader sortDirection={sortKey === 'budget' ? sortDir : false} onSort={() => toggleSort('budget')}>Daily</SortableHeader></div>
            <div className="text-center"><SortableHeader sortDirection={sortKey === 'budget' ? sortDir : false} onSort={() => toggleSort('budget')}>Monthly</SortableHeader></div>
          </div>

          <div className="px-4">
            {[...campaigns].sort((a, b) => {
              let cmp = 0;
              if (sortKey === 'name') cmp = (a.name || '').localeCompare(b.name || '');
              else if (sortKey === 'type') cmp = getCampaignType(a).localeCompare(getCampaignType(b));
              else if (sortKey === 'budget') cmp = (a.budget || 0) - (b.budget || 0);
              return sortDir === 'asc' ? cmp : -cmp;
            }).map(campaign => {
              const allocation = allocations.find(a => a.campaignId === campaign.id);
              const budget = Math.round(allocation?.budget ?? (campaign.budget || 0));
              const percentage = sliderMax > 0 ? Math.min(budget / sliderMax * 100, 100) : 0;
              // Show Google's recommended budget as reference dot when available
              const googleRecommendedBudget = (campaign as any).recommended_budget as number | undefined;
              const recommendedPct = googleRecommendedBudget && sliderMax > 0
                ? Math.min(googleRecommendedBudget / sliderMax * 100, 100)
                : undefined;
              // Detect generating campaigns (no ads, placeholder name)
              const isDraft = campaign.state === 'draft';
              const isGenerating = isDraft && (!campaign.ads || campaign.ads.length === 0) && !campaign.name?.includes(' | ');
              const isPaused = campaign.state === 'paused' && !!campaign.first_published_at;
              const hasPendingDeploy = campaign.state === 'live' && (campaign.budget_state === 'draft' || campaign.assets_state === 'draft' || campaign.settings_state === 'draft');
              // ERROR-state campaigns can't accept budget changes — per #4413 all mutations
              // on a failed campaign are disabled. Doesn't cover the DRAFT-with-failed-task
              // case (hasGenerationFailed inside CampaignStatusIcon) because that signal
              // requires a per-row useQuery which can't run inside this map callback.
              const isErrorState = campaign.state === 'error';
              const savedBudget = campaign.budget || 0;
              const hasBeenLive = !!campaign.first_published_at;
              const budgetChangeWarning = hasBeenLive && !isPaused && savedBudget > 0 && budget > 0 && Math.abs(budget - savedBudget) / savedBudget >= 0.2;
              const otherAllocated = allocations.filter(a => a.campaignId !== campaign.id).reduce((sum, a) => sum + Math.round(a.budget), 0);
              const noRemainingBudget = googleBudgetDaily > 0 && otherAllocated >= googleBudgetDaily && budget <= 0;
              const isCampaignDisabled = campaignInputsDisabled || isGenerating || noRemainingBudget || isErrorState;

              const isHighlighted = highlightCampaignId === campaign.id;

              return (
                <div
                  key={campaign.id}
                  ref={isHighlighted ? highlightRowRef : undefined}
                  className={`grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center py-3 border-b border-gray-100 last:border-b-0 ${isHighlighted ? '-mx-4 px-4 animate-[flash-green_2s_ease-out]' : ''} ${(isGenerating || noRemainingBudget || isErrorState) ? '[&>*:not(:first-child)]:opacity-40' : ''}`}
                >
                  <div className="flex items-center justify-center">
                    {isGenerating ? (
                      <Spinner size="sm" className="text-gray-400" />
                    ) : (
                      <button
                        onClick={() => {
                          if (!campaign.first_published_at) return;
                          if (campaign.state !== CAMPAIGN_STATE.PAUSED) {
                            setPauseConfirmCampaign(campaign);
                          } else {
                            handleTogglePause(campaign);
                          }
                        }}
                        disabled={togglingCampaignId === campaign.id || !campaign.first_published_at}
                        // Tooltip matches the icon's meaning instead of always describing pause
                        // semantics — the icon can be Loader / red ! / amber ! / Play / Pause
                        // depending on state, but the title used to say "must be published
                        // before paused" for every disabled-button case (#4425).
                        title={
                          togglingCampaignId === campaign.id
                            ? 'Updating…'
                            : campaign.state === CAMPAIGN_STATE.CREATING
                            ? 'Campaign is being created'
                            : campaign.state === CAMPAIGN_STATE.ERROR
                            ? 'Campaign has errors'
                            : campaign.state === CAMPAIGN_STATE.DRAFT
                            ? 'Campaign is in draft — publish to enable pause'
                            : !campaign.first_published_at
                            ? 'Campaign must be published before it can be paused'
                            : campaign.state === CAMPAIGN_STATE.LIVE && hasPendingDeploy
                            ? 'Campaign has unsaved changes — pause campaign'
                            : isPaused
                            ? 'Resume campaign'
                            : 'Pause campaign'
                        }
                        className={`shrink-0 ${campaign.first_published_at ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
                      >
                        <CampaignStatusIcon
                          campaign={campaign}
                          isToggling={togglingCampaignId === campaign.id}
                          isPaused={isPaused}
                          hasPendingDeploy={hasPendingDeploy}
                        />
                      </button>
                    )}
                  </div>
                  <div className="min-w-0">
                    <span className="text-sm font-medium truncate block flex items-center gap-2 text-gray-900">
                      {isGenerating ? 'Generating...' : (
                        <span
                          onClick={(e) => { e.stopPropagation(); navigate({ to: `/campaigns/google/${campaign.slug}` as string }); }}
                          className="hover:text-[#290BD9] hover:underline transition-colors truncate cursor-pointer"
                        >
                          {parseCampaignName(campaign.name)}
                        </span>
                      )}
                    </span>
                    {hasPendingDeploy && (
                      hasChanges ? (
                        <span className="text-xs font-medium text-gray-400 cursor-not-allowed" title="Save changes first">
                          Deploy Changes →
                        </span>
                      ) : (
                        <span
                          onClick={(e) => { e.stopPropagation(); navigate({ to: `/campaigns/google/${campaign.slug}` as string }); }}
                          className="text-xs font-medium text-[#290BD9] hover:text-[#2309C5] hover:underline transition-colors cursor-pointer"
                        >
                          Deploy Changes →
                        </span>
                      )
                    )}
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium ${getCampaignTypeStyle(campaign)}`}>
                      {getCampaignTypeLabel(campaign)}
                    </span>
                  </div>
                  <div className="px-1">
                    <Slider
                      value={percentage}
                      referenceValue={recommendedPct}
                      onChange={(value) => {
                        const desired = Math.round(value / 100 * sliderMax);
                        const otherAllocated = allocations.filter(a => a.campaignId !== campaign.id).reduce((sum, a) => sum + Math.round(a.budget), 0);
                        const maxForThis = Math.max(isDraft || isPaused ? 0 : 1, googleBudgetDaily - otherAllocated);
                        handleCampaignBudgetChange(campaign.id, Math.min(desired, maxForThis), isDraft || isPaused);
                      }}
                      disabled={isCampaignDisabled}
                    />
                  </div>
                  <div>
                    <div className={`flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto transition-colors ${isCampaignsOverPlatform ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300 focus-within:border-[#290BD9]'}`}>
                      <span className="text-sm text-gray-500">{cs}</span>
                      <input
                        type="text"
                        value={budget.toLocaleString()}
                        onChange={(e) => {
                          if (isCampaignDisabled) return;
                          const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                          handleCampaignBudgetChange(campaign.id, Math.max(0, value), isDraft || isPaused);
                        }}
                        onFocus={(e) => e.target.select()}
                        disabled={isCampaignDisabled}
                        className={`text-sm font-medium text-right bg-transparent border-none outline-none w-full focus:ring-0 p-0 ${isCampaignDisabled ? 'text-gray-400' : 'text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 text-center">~{cs}{(budget * 30).toLocaleString()}/m</div>
                  {budgetChangeWarning && !isGenerating && (
                    <div className="col-span-5 flex items-start gap-1.5 text-xs text-amber-600 -mt-1 mb-1">
                      <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                      <span>Large change may retrigger learning phase</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className={`border-t px-4 py-3 flex flex-col gap-2 ${isCampaignsOverPlatform ? 'bg-red-50' : 'bg-gray-50'}`}>
            {(() => {
              const pausedMonthly = allocations.reduce((sum, a) => {
                const c = campaigns.find(c => c.id === a.campaignId);
                return c?.state === 'paused' ? sum + Math.round(a.budget) * 30 : sum;
              }, 0);
              const activeMonthly = totalAllocatedMonthly - pausedMonthly;
              return (
                <>
                  <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(80px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center">
                    <div></div>
                    <div>
                      <span className={`text-sm font-semibold ${isCampaignsOverPlatform ? 'text-red-700' : 'text-gray-900'}`}>Allocated to Campaigns</span>
                      <span className={`text-xs block ${isCampaignsOverPlatform ? 'text-red-600' : isFullyAllocated ? 'text-green-600' : 'text-amber-600'}`}>
                        {isFullyAllocated ? 'Fully allocated' : isCampaignsOverPlatform ? <>{cs}{Math.round((totalAllocatedMonthly - googleBudget) / 30).toLocaleString()}/day over budget. <button type="button" onClick={() => setActiveTab('all')} className="underline hover:text-red-800">Increase account limit</button></> : googleBudget > 0 ? `${cs}${Math.round((googleBudget - totalAllocatedMonthly) / 30).toLocaleString()}/day remaining` : ''}
                      </span>
                      {pausedMonthly > 0 && (
                        <span className="text-xs text-gray-400 block">{cs}{activeMonthly.toLocaleString()} active, {cs}{pausedMonthly.toLocaleString()} paused</span>
                      )}
                    </div>
                    <div></div>
                    <div></div>
                    <div>
                      <div className="flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto border-gray-300">
                        <span className="text-sm text-gray-500">{cs}</span>
                        <span className="text-sm font-medium text-gray-900 text-right w-full">{totalAllocatedDaily.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 text-center">~{cs}{totalAllocatedMonthly.toLocaleString()}/m</div>
                  </div>
                </>
              );
            })()}
          </div>
        </Card>
      )}
    </div>
  );
  };

  const hasCampaignBudgets = campaigns.some(c => (c.budget || 0) > 0);
  const hasGoogleBudget = hasCampaignBudgets || ((maxDaily > 0 || (organizationData?.max_spend ?? 0) > 0) && (platformBudgets.google > 0 || (business?.platform_budgets?.find(b => b.platform === 'Google')?.amount ?? 0) > 0));

  const tabErrors: Record<string, boolean> = {
    all: isPlatformOverTotal,
    google: isCampaignsOverPlatform,
    meta: metaOverAllocated,
  };

  const tabs: { id: 'all' | 'google' | 'meta'; label: string; logo?: string; disabled: boolean }[] = [
    { id: 'all', label: 'Account Limits', disabled: false },
    { id: 'google', label: 'Google Ads', logo: '/google-ads.svg', disabled: false },
    { id: 'meta' as const, label: 'Meta Ads', logo: '/meta.png', disabled: false },
  ];

  const isCurrencyMissing = !organizationData?.default_currency;

  if (isCurrencyMissing) {
    return (
      <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
        <AlertCircle className="h-10 w-10 mb-4 text-[#B91C1C] opacity-60" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Your Currency First</h3>
        <p className="text-sm text-gray-500 max-w-md mb-6">
          Set your organisation currency before configuring budgets. Platform currencies must match your organisation currency.
        </p>
        <button
          onClick={() => navigate({ to: `/organizations/${organizationData?.id}` as string, hash: 'overview' })}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
        >
          Go to Organisation Settings
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-[#290BD9] text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } ${tab.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {tab.logo && <img src={tab.logo} className="h-4 w-4 object-contain" alt="" />}
              {tab.label}
              {tabErrors[tab.id] && activeTab !== tab.id && (
                <span className="h-4 w-4 rounded-full bg-[#B91C1C] text-white text-[10px] flex items-center justify-center font-bold shrink-0">!</span>
              )}
            </button>
          ))}
        </div>
      </div>


      {/* Tab content */}
      {activeTab === 'all' && renderAllPlatformsTab()}
      {activeTab === 'google' && (
        !hasGoogleAdsAccount ? (
          <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
            <img src="/google-ads.svg" className="h-10 w-10 mb-4 opacity-40" alt="Google Ads" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Google Ads</h3>
            <p className="text-sm text-gray-500 max-w-md mb-6">
              Connect your Google Ads account to start allocating budgets and managing campaigns.
            </p>
            <button
              onClick={() => navigate({ to: '/settings/account', hash: 'integrations' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
            >
              Connect Account
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </Card>
        ) : !hasGoogleBudget ? (
          <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
            <img src="/google-ads.svg" className="h-10 w-10 mb-4 opacity-40" alt="Google Ads" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Your Budget First</h3>
            <p className="text-sm text-gray-500 max-w-md mb-6">
              Configure and save your Maximum Budget and Google Ads allocation in the Account Limits tab to unlock campaign-level controls.
            </p>
            <button
              onClick={() => setActiveTab('all')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
            >
              Go to Account Limits
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </Card>
        ) : (
          renderCampaignTab('google')
        )
      )}
      {/* Stays mounted (CSS-hidden) while dirty or saving so staged Meta edits survive tab
          switches, exactly like the Google/Account Limits state living in this component. When
          clean and inactive it unmounts, so opening the dialog on other tabs fires no Meta
          queries (matters for orgs without a Meta connection). */}
      {(activeTab === 'meta' || metaDirty || isMetaSaving) && (
        <div className={activeTab === 'meta' ? '' : 'hidden'}>
          <MetaBudgetTab
            metaPlatformBudgetMonthly={platformBudgets.meta || 0}
            apiRef={metaApiRef}
            onDirtyChange={setMetaDirty}
            onOverAllocationChange={setMetaOverAllocated}
          />
        </div>
      )}

      {/* Save button — hidden when parent manages save, or on tabs with empty states */}
      <div className={`flex items-center justify-end gap-3 ${onSaveRef || activeTab === 'meta' || (activeTab === 'google' && campaigns.length === 0) ? 'hidden' : ''}`}>
        {saveError && <p className="text-sm text-[#B91C1C]">{saveError}</p>}
        {saveSuccess && <p className="text-sm text-[#047857] flex items-center gap-1"><Check className="h-4 w-4" />Saved</p>}
        {!saveError && !saveSuccess && maxDailyDelta !== 0 && savedMaxDaily > 0 && (
          <p className="text-xs text-amber-600">
            {maxDailyDelta > 0
              ? `Maximum budget will increase by ${cs}${maxDailyDelta.toLocaleString()}/day (${cs}${savedMaxDaily.toLocaleString()} → ${cs}${maxDaily.toLocaleString()})`
              : `Maximum budget will decrease by ${cs}${Math.abs(maxDailyDelta).toLocaleString()}/day (${cs}${savedMaxDaily.toLocaleString()} → ${cs}${maxDaily.toLocaleString()})`}
          </p>
        )}
        {/* #4719 — only surface the CTA once there are pending changes (or a save in flight);
            the "Saved" confirmation above still renders after hasChanges resets. */}
        {!onSaveRef && (hasChanges || isSaving) && (
          <SaveChangesButton className="h-9 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={!hasChanges || isPlatformOverTotal || isCampaignsOverPlatform || !!saveError} saving={isSaving} onClick={handleSaveAll} />
        )}
      </div>

      {/* Meta tab's save row — the same explicit-save pattern as the row above (#4719 CTA gating).
          Separate row because the messages differ and Google's save must not fire Meta's. */}
      <div className={`flex items-center justify-end gap-3 ${!onSaveRef && activeTab === 'meta' ? '' : 'hidden'}`}>
        {metaSaveError && <p className="text-sm text-[#B91C1C]">{metaSaveError}</p>}
        {metaSaveSuccess && <p className="text-sm text-[#047857] flex items-center gap-1"><Check className="h-4 w-4" />Saved</p>}
        {(metaDirty || isMetaSaving) && (
          <SaveChangesButton
            className="h-9 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!metaDirty || metaOverAllocated}
            saving={isMetaSaving}
            onClick={handleMetaSave}
          />
        )}
      </div>

      {UnsavedChangesModal}

      {/* Pause confirmation modal */}
      <ConformationModal
        isOpen={!!pauseConfirmCampaign}
        onClose={() => setPauseConfirmCampaign(null)}
        onConfirm={() => {
          if (pauseConfirmCampaign) {
            handleTogglePause(pauseConfirmCampaign);
            setPauseConfirmCampaign(null);
          }
        }}
        title={`Pause "${parseCampaignName(pauseConfirmCampaign?.name ?? '')}"`}
        description={`This campaign will stop spending. While paused, its ${cs}${(pauseConfirmCampaign?.budget || 0).toLocaleString()}/day budget can be reduced to as low as ${cs}0/day, reallocating the full amount to other campaigns.`}
        confirmText="Pause Campaign"
        cancelText="Keep Running"
        variant="warning"
      />
    </div>
  );
}
